<template>
  <div>
    <BaseError
      v-if="quoteError"
      :class="{'quoteError': quoteError}"
      @reload="onReload()"
    />
    <BaseForm v-else :loading="loading" :errorMessage="errorMessage" class="noprogress">
      <template v-slot:header>
        {{$t('qic_quote_header')}}
      </template>
      <div class="quote">
        <QuoteRenewInformation
          :productName="productName"
          :productType="productType"
          :policyNumber="policyInfo.policyNo"
          :productTitle="productTitle"
          disableInfoList
          :startPolicyDate="startQuoteDate"
          :endPolicyDate="endQuoteDate"/>
        <QuoteInclusive
          :quoteInclusivePrice="price"
          :priceWithoutDiscount="planDiscount &&  fullPlanPrice"
          :includes="included"
          :discountsArray="planDiscounts"
          :quoteDiscount="planDiscountPercent"
          :deductibleValue="deductibleValue"
          :quoteAdditionalPrice="fullAdditionalPrice"/>
        <QuoteBoatInfo
          v-if="productType ==='boat'"
          :info="boatInfo"
        />
        <QuotePolicyOwner
          :fullName="userInfo.fullName"
          :qid="userInfo.qid"
          :email="userInfo.email"
          :phone="userInfo.mobileNumber">
        </QuotePolicyOwner>
        <div class="quote__additional" v-if="productType==='home' && additionals.length">
          <div class="quote__header">
            <div class="quote__header-top">
              <div class="quote__title">{{$t('qic_quote_additional_header')}}
              </div>
              <div class="quote__price" v-if="fullAdditionalPrice">{{$t('qic_qar')}} {{ fullAdditionalPrice }}</div>
            </div>
          </div>
          <div class="quote__list">
            <HomeAdditionalItem
              v-for="(additional, index) of additionals"
              :key="index"
              :additional="additional"
              :covers="homeAdditionalCovers[additional.id].values"
              :checked="isHomeAdditionalChecked(additional.id)"
              @toggleSwitch="toggleHomeAdditional"
              @changeCover="changeHomeCover"/>
          </div>
        </div>
        <QuoteAdditionals
          v-if="productType !=='home' && additionals.length"
          :additionals="additionals"
          :selectedAdditionals="selectedAdditionals"
          :quoteAdditionalPrice="fullAdditionalPrice"
          @changeSelectedAdditionals="onChangeAdditionals"/>
        <QuoteCheckout
          :quoteInclusivePrice="fullPlanPrice"
          :quoteTotalPrice="paymentPrice"
          :totalPriceLoading="totalPriceLoading"
          :quoteDiscountOnline="planDiscount"
          :quoteAdditionalPrice="fullAdditionalPrice"
          :paymentPlanSwitch="isSwitcherPymentPlanOn"
          :promocode="promocode"
          :discountsArray="planDiscounts"
          :quoteNumber="quoteNumber"
          :termsHref="termsLink"
          @applePaySucceed="succeedApplePay"
          :getApplePayTotalCallback="getTotalPriceApplePay.bind(this)"
          @onChangePaymentPlan="onChangePlan"
          @onPayment="onSubmit"
          @chooseCard="onChooseCard"
          @setQuoteError="setQuoteError"/>
      </div>
    </BaseForm>
  </div>
</template>

<script>
import prodCodeMap from '@/assets/maps/prodCodeMap.json'
import homeAdditionalCovers from '@/assets/maps/homeAdditionalCovers.json'
import HomeAdditionalItem from '@/components/HomeAdditionalItem.vue'
import QuoteBoatInfo from '@/components/quote/QuoteBoatInfo.vue'
import { trackGtag } from '@/utils/useGtag'

import {
  QuoteInclusive,
  QuoteAdditionals,
  QuoteCheckout,
  BaseForm,
  QuotePolicyOwner,
  BaseError,
  QuoteRenewInformation
} from 'qic-components'
import { debounce } from '../utils/debounce'
import { TPL_CODE } from '@/constants/constants.ts'
import { getErrorMessage } from '@/shared/errors/getErrorMessage'

export default {
  name: 'PaymentComprehensiveQuote',
  components: {
    QuoteInclusive,
    QuoteAdditionals,
    QuoteCheckout,
    BaseForm,
    HomeAdditionalItem,
    QuotePolicyOwner,
    BaseError,
    QuoteRenewInformation,
    QuoteBoatInfo
  },
  data () {
    return {
      quoteError: false,
      loading: false,
      productType: this.$store.state.renew.productType || '',
      policyInfo: this.$store.state.renew.policyInfo || {},
      selectedAdditionals: [],
      paymentPlan: 'annual',
      additionals: [],
      included: [],
      quote: null,
      totalPrice: null,
      pricePerMonth: null,
      errorMessage: null,
      totalPriceLoading: false,
      homeAdditionalCovers,
      boatInfo: {},
      quoteNumber: null,
      choosenCard: 'credit-card',
      termsLink: null
    }
  },
  computed: {
    price () {
      return Number(this.quote?.price) || 0
    },
    paymentPrice () {
      return this.paymentPlan === 'annual' ? this.totalPrice : this.pricePerMonth
    },
    discountPercent () {
      return this.planDiscount ? 10 : 0
    },
    planDiscount () {
      if (this.quote?.discount) {
        return Number(this.quote?.discount)
      }
      return 0
    },
    planDiscountPercent () {
      if (this.planDiscount) return (this.planDiscount * 100 / this.fullPlanPrice).toFixed(0)
      return 0
    },
    planDiscounts () {
      if (this.quote?.discounts) {
        return this.quote?.discounts
      }
      return []
    },
    fullPlanPrice () {
      return this.price + this.planDiscount
    },
    inclusiveCovers () {
      return this.included.reduce((prev, item) => prev + Number(item.value), 0)
    },
    submitDisabled () {
      return !this.acceptance
    },
    fullAdditionalPrice () {
      return this.additionals.reduce((acc, add) => {
        if (this.selectedAdditionals.includes(add.id)) return acc + Number(add.value)
        return acc
      }, 0)
    },
    deductibleValue () {
      const deductible = this.quote?.deductible
      return deductible ? Number(deductible) : null
    },
    productStaticIncludes () {
      const includes = this.productInfo?.staticIncludes ? [...this.productInfo?.staticIncludes] : []
      return includes
        .map(i => {
          i.name = this.$t(i.name)
          i.description = this.$t(i.description)
          return i
        })
    },
    productInfo () {
      return prodCodeMap
        .filter(prod => prod.type === this.productType)
        .find(prod => prod.code === this.policyInfo?.prodCode)
    },
    productName () {
      return this.$t(this.productInfo.nameT)
    },
    productTitle () {
      if (this.productType === 'boat' && this.policyInfo?.vesselName) {
        return this.policyInfo.vesselName
      }
      return this.$t(this.productInfo.productTitleT)
    },
    disabledProductAdditionals () {
      return this.productInfo?.disabledAdditionals || []
    },
    isSwitcherPymentPlanOn () {
      return this.productInfo?.monthlyPayment || false
    },
    docName () {
      if (this.productType === 'car' || this.productType === 'moto') {
        if (this.carProductName === 'Car Comprehensive') {
          if (this.paymentPlan === 'annual') {
            return 'carodtc'
          } else {
            return 'carinstalltc'
          }
        } else {
          return 'cartpltc'
        }
      } else if (this.productType === 'boat') {
        if (this.carProductName === 'Car Comprehensive') {
          return 'boadodtc'
        } else {
          return 'boattpltc'
        }
      } else if (this.productType === 'home') {
        return 'hometc'
      }

      return ''
    },
    locale () {
      return this.$i18n.locale
    },
    isRtl () {
      return this.locale === 'ar'
    },
    userInfo () {
      if (!this.policyInfo) return {}
      return {
        fullName: this.policyInfo.insName,
        qid: this.policyInfo.civilId,
        mobileNumber: !this.policyInfo.mobileNo
          ? ''
          : (this.policyInfo.mobileNo.length === 8)
              ? `+974${this.policyInfo.mobileNo}`
              : `+${this.policyInfo.mobileNo}`,
        email: this.policyInfo.emailId
      }
    },
    promocode () {
      return this.$store.state.renew?.promocode || ''
    },
    startQuoteDate () {
      if (!this.policyInfo.polEndDate) return new Date()
      const endDate = new Date(this.policyInfo.polEndDate)
      const cur = new Date()
      return endDate < cur ? cur : new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1)
    },
    endQuoteDate () {
      const start = this.startQuoteDate
      return new Date(start.getFullYear() + 1, start.getMonth(), start.getDate() - 1)
    }
  },
  methods: {
    setQuoteError (is) {
      this.quoteError = is
    },
    onReload () {
      location.reload()
    },
    onChooseCard (cardData) {
      this.choosenCard = cardData.paymentMethod
      // console.log(eventData)
      // trackEvent('chooseCard', eventData)
    },
    onSubmit ({ paymentMethod }) {
      const productVariant = this.carProductName === 'Car Comprehensive' ? 'OD' : 'TPL'
      const eventData = {
        event: 'purchase',
        coupon: this.promocode,
        transaction_id: this.quoteNumber,
        policy_id: this.policyInfo.policyNo,
        number_of_addons: this.selectedAdditionals.length,
        plan_name: productVariant,
        total_price: this.paymentPrice,
        choosen_card: this.choosenCard
      }

      localStorage.setItem('eventData', JSON.stringify(eventData))
      trackGtag('complete_purchase', {
        page: 'your_quote',
        coupon: this.promocode,
        transaction_id: this.quoteNumber,
        policy_id: this.policyInfo.policyNo,
        number_of_addons: this.selectedAdditionals.length,
        plan_name: productVariant,
        total_price: this.paymentPrice,
        choosen_card: this.choosenCard
      })

      this.loading = true
      this.$store.dispatch('redirectToRenewPayment', {
        ids: this.selectedAdditionals,
        paymentPlan: this.paymentPlan,
        paymentMethod
      })
        .then(() => {
          this.$store.commit('saveLocale', this.$i18n.locale)
        })
        .catch((error) => {
          this.errorMessage = getErrorMessage(error)
        })
    },
    updateTotalPrice () {
      this.totalPriceLoading = true
      return this.$store.dispatch('getRenewPrice', {
        ids: this.selectedAdditionals,
        selectproduct: false
      })
        .then(({ totalPrice, perMonth }) => {
          this.totalPrice = totalPrice
          this.pricePerMonth = perMonth
        })
        .catch((error) => {
          this.errorMessage = getErrorMessage(error)
        })
        .finally(() => {
          this.totalPriceLoading = false
        })
    },
    onChangeAdditionals ({ newAdditionals }) {
      const removedAdditionals = this.selectedAdditionals.filter(id => !newAdditionals.includes(id))
      const addedAdditionals = newAdditionals.filter(id => !this.selectedAdditionals.includes(id))
      const productVariant = this.carProductName === 'Car Comprehensive' ? 'OD' : 'TPL'

      const params = {
        page: 'quote',
        plan_name: this.$store.state.renew?.policyInfo?.prodCode === TPL_CODE ? 'TPL' : 'OD',
        chosen_plan: productVariant,
        total_price: this.totalPrice,
        quote_id: this.quoteNumber
      }

      removedAdditionals.forEach(removedAdditionalId => {
        const removedAdditional = this.additionals.find(additional => additional.id === removedAdditionalId)

        if (removedAdditional) {
          trackGtag('switch_addon', {
            ...params,
            addon_name: removedAdditional.name,
            addon_price: removedAdditional.value,
            state: false
          })
        }
      })

      addedAdditionals.forEach(addedAdditionalId => {
        const addedAdditional = this.additionals.find(additional => additional.id === addedAdditionalId)
        if (addedAdditional) {
          trackGtag('switch_addon', {
            ...params,
            addon_name: addedAdditional.name,
            addon_price: addedAdditional.value,
            state: true
          })
        }
      })
      this.selectedAdditionals = newAdditionals
      this.updateTotalPriceDebounce()
    },
    onChangePlan (plan) {
      this.paymentPlan = plan
    },
    selectAllAdditionals () {
      this.additionals.forEach((add) => {
        this.selectedAdditionals.push(add.id)
      })
    },
    isHomeAdditionalChecked (id) {
      return this.selectedAdditionals.includes(id)
    },
    toggleHomeAdditional (id) {
      if (this.isHomeAdditionalChecked(id)) {
        this.selectedAdditionals = this.selectedAdditionals.filter(additional => additional !== id)
      } else {
        this.selectedAdditionals.push(id)
      }
      this.updateTotalPriceDebounce()
    },
    changeHomeCover ({ id, value }) {
      this.toggleHomeAdditionalLoader(id, true)
      this.$store.dispatch('changeAdditionalCoverPrice', { value, coverCode: id })
        .then((newAdditionalInfo) => {
          this.additionals.filter(a => a.id === id).forEach(a => {
            a.value = newAdditionalInfo.value
            a.cover = newAdditionalInfo.cover
          })
          if (this.isHomeAdditionalChecked(id)) this.updateTotalPriceDebounce()
        })
        .finally(() => {
          this.toggleHomeAdditionalLoader(id, false)
        })
    },
    toggleHomeAdditionalLoader (id, state) {
      this.additionals.filter(a => a.id === id).forEach(a => { a.loader = state })
    },
    getTotalPriceApplePay () {
      return this.$store.dispatch('getTotalPrice', {
        quoteno: this.quoteNumber,
        schemecode: this.quote.schemecode,
        productcode: this.quote.productcode,
        selectproduct: true
      })
        .then((res) => {
          return res
        })
    },
    succeedApplePay () {
      this.$router.replace({ name: 'PaymentInsuranceSuccess' })
    },
    getBoatLists () {
      return Promise.allSettled([
        this.$store.dispatch('getBoatRegistryPorts'),
        this.$store.dispatch('getBoatEngines')
      ]).then(([ports, engines]) => {
        return {
          ports: ports?.value,
          engines: engines?.value
        }
      })
    },
    setBoatInfo (policy, engines, ports) {
      const engineData = engines?.length && engines.find(engine => policy?.motorEngin === engine.code)
      const portData = ports?.length && ports.find(port => policy?.portOfRegistry === port.code)
      this.boatInfo = {
        seatingCapacity: policy?.seatingCapacity,
        yearOfBuilt: policy?.modelYear,
        regNumber: policy?.regNo,
        portOfRegistry: this.isRtl ? portData?.descAr : portData?.desc,
        engine: this.isRtl ? engineData?.descAr : engineData?.desc,
        numberEngines: policy?.noOfEngines
      }
    },
    updateTotalPriceDebounce () {}
  },
  beforeMount  () {
    this.$store.dispatch('getQatarWordings', { docname: this.docName })
      .then(({ data }) => {
        if (!data?.Wordings[0].length) {
          this.termsLink = data?.Wordings[0].Link
        }
      })
  },
  mounted () {
    setTimeout(() => {
      const productVariant = this.carProductName === 'Car Comprehensive' ? 'OD' : 'TPL'
      const ecommerceData = {
        items: [
          {
            item_name: this.carProductName === 'Car Comprehensive' ? 'Comprehensive Insurance' : 'Third Party Liability',
            price: this.paymentPrice,
            item_category: 'Car Insurance',
            item_variant: productVariant,
            covers: this.included.map(addons => addons.name)
          }
        ]
      }

      trackGtag('add_to_cart', {
        ecommerce: ecommerceData
      })
    }, 6000)
    // Debounce connect for additional change
    this.updateTotalPriceDebounce = debounce(() => this.updateTotalPrice(), 600)

    this.loading = true
    this.quote = this.$store.state.renew.renewScheme
    Promise.all([
      this.$store.dispatch('getRenewQuote')
    ])
      .then(([quote]) => {
        this.quote = quote.scheme
        this.quoteNumber = quote.quoteNumber
        this.additionals = this.quote.ids
          .map(add => ({ ...add, loader: false }))
          .filter(add => !this.disabledProductAdditionals.includes(add.id))
        const staticIncludes = this.productStaticIncludes.filter(s => !this.quote.included.find(include => include.id === s.id))
        this.included = [...staticIncludes, ...this.quote.included]
        if (this.productType === 'car') {
          this.selectAllAdditionals()
        }
        if (this.productType === 'boat') {
          return this.getBoatLists()
            .then(({ ports, engines }) => {
              this.setBoatInfo(this.policyInfo, engines, ports)
            })
        }
        return Promise.resolve()
      })
      .then(() => {
        return this.updateTotalPrice()
      })
      .finally(() => {
        this.loading = false
      })
      .catch((error) => {
        this.errorMessage = getErrorMessage(error)
      })

    const params = this.getAnalyticsData()

    trackGtag('view_page', {
      page: 'quote',
      ...params,
      chosen_plan: this.carProductName === 'Car Comprehensive' ? 'OD' : 'TPL',
      total_price: this.totalPrice
    })
  }
}
</script>

<style lang="scss">
.quoteError {
  margin-top: 0px;
}
</style>
